import { useEffect, useRef, useState } from 'react';
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

type Bucket = {
  bucket: string; stored: number; distinct_calls: number; inbound: number; outbound: number;
  internal: number; intl: number; pstn: number; answered: number; missed: number; abandoned: number;
  e911: number; cc: number; direct: number; aa: number; huntq: number;
};
type Dash = { grain: string; count: number; series: Bucket[]; totals: Record<string, number>; latest_day: string | null };
type Cc = { totals?: Record<string, number>; by_queue?: any[]; by_menu?: any[]; coverage?: any };

const N = (x: any) => (Number(x) || 0).toLocaleString();
const secs = (s: any) => { const n = Number(s) || 0; return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`; };
const iso = (d: Date) => d.toISOString().slice(0, 10);
function Tile({ v, l }: { v: any; l: string }) {
  return <div className="card p-2 text-center"><div className="text-base md:text-lg font-semibold leading-tight">{v}</div><div className="text-[10px] text-gray-500 leading-tight">{l}</div></div>;
}

const COLS: [keyof Bucket | 'bucket', string][] = [
  ['bucket', 'Period'], ['distinct_calls', 'Distinct'], ['inbound', 'Inbound'], ['outbound', 'Outbound'],
  ['internal', 'Internal'], ['pstn', 'PSTN'], ['intl', 'International'], ['missed', 'Missed'],
  ['cc', 'Contact Ctr'], ['e911', '911'], ['stored', 'Stored rows'],
];
const WXC_TILES: [string, string][] = [
  ['distinct_calls', 'Distinct calls'], ['inbound', 'Inbound'], ['outbound', 'Outbound'],
  ['internal', 'Internal'], ['missed', 'Missed'], ['e911', '911'],
];

export default function CdrDashboard() {
  const [grain, setGrain] = useState<'day' | 'week' | 'month'>('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [d, setD] = useState<Dash | null>(null);
  const [cc, setCc] = useState<Cc | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  async function load() {
    setBusy(true); setErr(null);
    const ccFrom = from || iso(new Date(Date.now() - 365 * 86400000));
    const ccTo = (to || iso(new Date())) + 'T23:59:59Z';
    try {
      const p = new URLSearchParams({ grain });
      if (from) p.set('from', from);
      if (to) p.set('to', to);
      const [wr, cr] = await Promise.all([
        fetch('/cdr/api/dashboard?' + p.toString(), { credentials: 'include' }),
        fetch('/cdr/api/cc?' + new URLSearchParams({ from: ccFrom, to: ccTo }).toString(), { credentials: 'include' }),
      ]);
      const wj = await wr.json();
      if (!wr.ok) { setErr(wj.error || 'error'); setD(null); } else { setD(wj); }
      if (cr.ok) { try { setCc(await cr.json()); } catch { setCc(null); } } else setCc(null);
    } catch (e: any) { setErr(String(e)); }
    setBusy(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [grain]);

  useEffect(() => {
    if (!canvasRef.current || !d) return;
    const labels = d.series.map(b => b.bucket);
    const ds = (k: keyof Bucket, color: string, label: string) => ({ label, data: d.series.map(b => b[k] as number), backgroundColor: color, stack: 'x' });
    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: { labels, datasets: [ds('inbound', '#2f6fed', 'Inbound'), ds('outbound', '#e8873a', 'Outbound'), ds('internal', '#3aa76d', 'Internal'), ds('intl', '#b23ac0', 'International')] },
      options: {
        responsive: true, maintainAspectRatio: false,
        scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, ticks: { precision: 0 } } },
        plugins: { legend: { position: 'bottom' } },
      },
    });
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [d]);

  const T = d?.totals || {};
  const C = cc?.totals || {};

  function dl() {
    if (!d) return;
    const rows = [COLS.map(c => c[1]).join(',')]
      .concat(d.series.map(b => COLS.map(c => (c[0] === 'bucket' ? b.bucket : (b as any)[c[0]])).join(',')));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cdr_dashboard_${grain}.csv`;
    a.click();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-3 flex-wrap">
        <div className="text-base font-semibold">Unified dashboard</div>
        <div className="text-[11px] text-gray-500">Webex Calling (WxC) and Webex Contact Center (WxCC) at a glance</div>
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        {(['day', 'week', 'month'] as const).map(g => (
          <button key={g} onClick={() => setGrain(g)}
            className={`px-3 py-1.5 rounded-md text-xs ${grain === g ? 'bg-tnh-accent text-white' : 'bg-gray-100 dark:bg-gray-800'}`}>
            {g[0].toUpperCase() + g.slice(1)}
          </button>
        ))}
        <span className="text-xs text-gray-500 ml-2">From</span>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border rounded-md px-2 py-1 text-xs bg-transparent" />
        <span className="text-xs text-gray-500">To</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border rounded-md px-2 py-1 text-xs bg-transparent" />
        <button onClick={load} className="px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs">Apply</button>
        <button onClick={dl} className="px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs">Export WxC CSV</button>
        {busy && <span className="text-xs text-gray-500">Loading&hellip;</span>}
      </div>

      {err && <div className="text-xs text-red-500">Sign in to the CDR portal to view ({err}).</div>}

      {(d || cc) && (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* ── Webex Calling (WxC) — left half ── */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#2f6fed' }} />
              <div className="text-sm font-semibold">Webex Calling — WxC</div>
              {d && <span className="text-[11px] text-gray-400 ml-auto">through {d.latest_day || '?'}</span>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {WXC_TILES.map(t => <Tile key={t[0]} v={N((T as any)[t[0]])} l={t[1]} />)}
            </div>
            <div className="card p-3">
              <div className="text-xs text-gray-500 mb-2">Call composition by {grain} &mdash; Inbound / Outbound / Internal / International</div>
              <div style={{ height: '12.5rem' }}><canvas ref={canvasRef} /></div>
            </div>
          </div>

          {/* ── Webex Contact Center (WxCC) — right half ── */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#00CF64' }} />
              <div className="text-sm font-semibold">Webex Contact Center — WxCC</div>
              {cc?.coverage && <span className="text-[11px] text-gray-400 ml-auto">{N(cc.coverage.n)} tasks</span>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Tile v={N(C.calls)} l="CC calls" />
              <Tile v={N(C.handled)} l="Handled" />
              <Tile v={N(C.abandoned)} l="Abandoned" />
              <Tile v={secs(C.avg_wait_s)} l="Avg wait" />
              <Tile v={secs(C.avg_talk_s)} l="Avg talk" />
              <Tile v={N(C.after_hours)} l="After hours" />
            </div>
            <div className="card p-3 overflow-auto" style={{ height: '12.5rem' }}>
              <div className="text-xs text-gray-500 mb-2">Queues &amp; most‑selected menu options</div>
              <table className="w-full text-xs">
                <thead className="text-gray-500"><tr><th className="text-left px-2 py-1">Queue / menu option</th><th className="text-right px-2 py-1">Calls</th></tr></thead>
                <tbody>
                  {(cc?.by_queue || []).slice(0, 4).map((q: any, i: number) => (
                    <tr key={'q' + i} className="border-t border-gray-100 dark:border-gray-800"><td className="px-2 py-1">{q.k || '(none)'}</td><td className="px-2 py-1 text-right">{N(q.calls)}</td></tr>
                  ))}
                  {(cc?.by_menu || []).slice(0, 5).map((m: any, i: number) => (
                    <tr key={'m' + i} className="border-t border-gray-100 dark:border-gray-800"><td className="px-2 py-1 text-gray-500">↳ {m.k}</td><td className="px-2 py-1 text-right">{N(m.calls)}</td></tr>
                  ))}
                  {!(cc?.by_queue || []).length && !(cc?.by_menu || []).length && <tr><td colSpan={2} className="px-2 py-3 text-center text-gray-400">No contact‑center data in range.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {d && (
        <div className="card p-3 overflow-auto">
          <div className="text-xs text-gray-500 mb-2">WxC detail by {grain}</div>
          <table className="w-full text-xs">
            <thead className="text-gray-500"><tr>{COLS.map(c => <th key={c[0]} className={c[0] === 'bucket' ? 'text-left px-2 py-1' : 'text-right px-2 py-1'}>{c[1]}</th>)}</tr></thead>
            <tbody>
              {d.series.map(b => (
                <tr key={b.bucket} className="border-t border-gray-100 dark:border-gray-800">
                  {COLS.map(c => <td key={c[0]} className={c[0] === 'bucket' ? 'text-left px-2 py-1' : 'text-right px-2 py-1'}>{c[0] === 'bucket' ? b.bucket : N((b as any)[c[0]])}</td>)}
                </tr>
              ))}
              <tr className="font-semibold border-t border-gray-300 dark:border-gray-700">
                <td className="text-left px-2 py-1">TOTAL</td>
                {COLS.slice(1).map(c => <td key={c[0]} className="text-right px-2 py-1">{N((T as any)[c[0]])}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
